import { errorMsg } from '../utils/errorMsg';
import React, { useState, useEffect } from 'react';
import { Box, Grid, Card, Typography, CircularProgress, Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import { registerCharts, barOptions, barDataset, CHART } from '../components/ChartTheme';
import AppLayout from '../components/AppLayout';
import api from '../axios';
import DepartmentIcon from '@mui/icons-material/AccountBalance';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

registerCharts();

export default function VoluntariadoDashboard() {
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  useEffect(() => {
    api.get('/voluntariado/dashboard')
      .then(res => setDash(res.data))
      .catch(err => setErro(errorMsg(err) || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, []);

  const kpis = [
    { icon: <DepartmentIcon />, value: dash?.departamentos, label: 'Departamentos' },
    { icon: <EventIcon />, value: dash?.eventos, label: 'Eventos ativos' },
    { icon: <PeopleIcon />, value: dash?.voluntarios, label: 'Voluntários' },
    { icon: <AssignmentIcon />, value: dash?.escalas_pendentes, label: 'Escalas pendentes' },
    { icon: <CheckCircleIcon />, value: dash?.escalas_confirmadas, label: 'Escalas confirmadas' },
  ];

  const porDepto = Array.isArray(dash?.eventos_por_departamento) ? dash.eventos_por_departamento : [];

  const atalhos = [
    { label: 'Voluntariado', icon: <PeopleIcon />, path: '/voluntariado' },
    { label: 'Gestão', icon: <ManageAccountsIcon />, path: '/gestao-voluntariado' },
  ];

  return (
    <AppLayout>
      <Box sx={{ py: { xs: 2, md: 3 } }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ color: CHART.teal, fontWeight: 700, letterSpacing: 1, mb: 0.5, textTransform: 'uppercase', fontSize: 12 }}>Módulo</Typography>
          <Typography variant="h1" sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, lineHeight: 1.2, fontWeight: 800 }}>Voluntariado</Typography>
          <Typography sx={{ color: 'text.secondary', mt: 1 }}>Organização por departamentos, eventos e escala de voluntários.</Typography>
        </Box>

        {loading && <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>}
        {erro && <Alert severity="error" sx={{ mb: 3 }}>{erro}</Alert>}

        {dash && (
          <>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {kpis.map((k, i) => (
                <Grid item xs={6} md={2.4} key={i}>
                  <Card elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 46, height: 46, borderRadius: 1, bgcolor: 'rgba(0,241,167,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: CHART.teal, flexShrink: 0 }}>{k.icon}</Box>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '4.4rem', color: 'text.primary', lineHeight: 1 }}>{k.value ?? '—'}</Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.78rem' }}>{k.label}</Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Card elevation={0} sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', p: 3, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <TrendingUpIcon sx={{ color: CHART.teal }} />
                <Typography variant="h3" sx={{ fontSize: '1rem' }}>Eventos por departamento</Typography>
              </Box>
              <Box sx={{ height: 240 }}>
                <Bar data={{ labels: porDepto.map(d => d.departamento), datasets: [{ ...barDataset(porDepto.map(d => d.total)), label: 'Eventos' }] }} options={barOptions()} />
              </Box>
            </Card>

            <Typography variant="h3" sx={{ fontSize: '1rem', fontWeight: 700, mb: 2 }}>Atalhos</Typography>
            <Grid container spacing={1.5}>
              {atalhos.map(a => (
                <Grid item xs={12} sm={6} md={3} key={a.path}>
                  <Button variant="outlined" onClick={() => navigate(a.path)} endIcon={<ArrowForwardIcon />} fullWidth
                    sx={{ justifyContent: 'space-between', borderColor: 'rgba(0,241,167,0.4)', color: CHART.teal, fontWeight: 700, borderRadius: 1, px: 2, py: 1.2, '&:hover': { borderColor: CHART.teal, bgcolor: 'rgba(0,241,167,0.08)' } }}>
                    {a.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
    </AppLayout>
  );
}
